import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Feather } from '@expo/vector-icons';
import Svg, { Circle } from 'react-native-svg';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaSpacing, PaprikaRadius } from '../../styles/theme';

interface RecipeCoverageBannerProps {
  coverage: { have: number; buy: number; total: number };
  onAddToGrocery: () => void;
  onOpenPantry: () => void;
}

const RING_SIZE = 58;
const RING_RADIUS = 23;
const RING_STROKE = 5;
const CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/**
 * Coverage banner above the ingredient list — replaces the old "cochez ce que
 * vous avez" bar. The pantry is the source of truth: it shows how much of the
 * recipe is covered and carries the single action "Ajouter les N à l'épicerie".
 */
export const RecipeCoverageBanner: React.FC<RecipeCoverageBannerProps> = ({
  coverage,
  onAddToGrocery,
  onOpenPantry,
}) => {
  const { t } = useTranslation();
  const { have, buy, total } = coverage;
  const allCovered = buy === 0;
  const ringColor = allCovered ? Paprika.success : Paprika.accent;
  const progress = total > 0 ? have / total : 0;

  return (
    <View style={[styles.card, allCovered ? styles.cardAllGood : styles.cardBuy]}>
      <View style={styles.topRow}>
        <View style={styles.ringWrap}>
          <Svg width={RING_SIZE} height={RING_SIZE}>
            <Circle
              cx={RING_SIZE / 2}
              cy={RING_SIZE / 2}
              r={RING_RADIUS}
              stroke={Paprika.hairline}
              strokeWidth={RING_STROKE}
              fill="none"
            />
            <Circle
              cx={RING_SIZE / 2}
              cy={RING_SIZE / 2}
              r={RING_RADIUS}
              stroke={ringColor}
              strokeWidth={RING_STROKE}
              strokeLinecap="round"
              strokeDasharray={`${CIRCUMFERENCE * progress} ${CIRCUMFERENCE}`}
              transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
              fill="none"
            />
          </Svg>
          <Text style={styles.ringLabel}>{`${have}/${total}`}</Text>
        </View>

        <View style={styles.titleBlock}>
          {allCovered ? (
            <Text style={styles.title}>
              {t('recipeDetail.coverageAllPrefix', 'Vous avez ')}
              <Text style={[styles.titleCount, { color: Paprika.success }]}>
                {t('recipeDetail.coverageAllWord', 'tout')}
              </Text>
              {t('recipeDetail.coverageAllSuffix', ' pour cuisiner')}
            </Text>
          ) : (
            <Text style={styles.title}>
              {t('recipeDetail.coveragePrefix', 'Il vous manque ')}
              <Text style={styles.titleCount}>
                {buy}{' '}
                {buy > 1
                  ? t('recipeDetail.ingredientsWord', 'ingrédients')
                  : t('recipeDetail.ingredientWord', 'ingrédient')}
              </Text>
            </Text>
          )}
          <Text style={styles.subtitle}>
            {allCovered
              ? t('recipeDetail.coverageSubAll', 'Croisé avec votre garde-manger — basiques inclus.')
              : t('recipeDetail.coverageSub', {
                  defaultValue:
                    'Croisé avec votre garde-manger — les {{count}} autres, vous les avez déjà (basiques inclus).',
                  count: have,
                })}
          </Text>
        </View>
      </View>

      {!allCovered && (
        <TouchableOpacity style={styles.addButton} onPress={onAddToGrocery} activeOpacity={0.85}>
          <Feather name="shopping-cart" size={16} color="#fff" />
          <Text style={styles.addButtonText}>
            {t('recipeDetail.addBuyToGrocery', {
              defaultValue: "Ajouter les {{count}} à l'épicerie",
              count: buy,
            })}
          </Text>
        </TouchableOpacity>
      )}

      <View style={[styles.separator, allCovered && styles.separatorAllGood]} />

      <View style={styles.legendRow}>
        <View style={styles.legendGroup}>
          <View style={styles.legendItem}>
            <View style={[styles.miniMark, { backgroundColor: Paprika.successSoft }]}>
              <Feather name="check" size={10} color={Paprika.success} />
            </View>
            <Text style={styles.legendText}>{t('recipeDetail.legendHave', 'Déjà chez vous')}</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.miniMark, { backgroundColor: Paprika.brandTint }]}>
              <Feather name="shopping-cart" size={10} color={Paprika.accent} />
            </View>
            <Text style={styles.legendText}>{t('recipeDetail.legendBuy', 'À acheter')}</Text>
          </View>
        </View>
        <TouchableOpacity onPress={onOpenPantry} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
          <Text style={styles.pantryLink}>
            {t('recipeDetail.editPantry', 'Modifier mon garde-manger')}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    marginHorizontal: PaprikaSpacing.lg,
    marginBottom: PaprikaSpacing.md,
    borderRadius: 18,
    padding: PaprikaSpacing.md,
  },
  cardBuy: {
    backgroundColor: Paprika.brandTint,
  },
  cardAllGood: {
    backgroundColor: Paprika.successSoft,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.md,
  },
  ringWrap: {
    width: RING_SIZE,
    height: RING_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ringLabel: {
    position: 'absolute',
    fontFamily: 'Inter-SemiBold',
    fontSize: 12.5,
    color: Paprika.textPrimary,
    fontVariant: ['tabular-nums'],
  },
  titleBlock: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 20,
    lineHeight: 24,
    color: Paprika.textPrimary,
  },
  titleCount: {
    fontFamily: 'Newsreader-Italic',
    color: Paprika.accent,
  },
  subtitle: {
    fontFamily: 'Inter-Regular',
    fontSize: 11.5,
    lineHeight: 16,
    color: Paprika.textMuted,
    marginTop: 4,
  },
  addButton: {
    marginTop: PaprikaSpacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: PaprikaSpacing.sm,
    backgroundColor: Paprika.accent,
    borderRadius: PaprikaRadius.full,
    paddingVertical: 13,
  },
  addButtonText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 14.5,
    color: '#fff',
  },
  separator: {
    height: 1,
    backgroundColor: 'rgba(134, 78, 44, 0.14)',
    marginTop: PaprikaSpacing.md,
    marginBottom: 10,
  },
  separatorAllGood: {
    backgroundColor: 'rgba(120, 165, 90, 0.2)',
  },
  legendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: PaprikaSpacing.xs,
  },
  legendGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.md,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  miniMark: {
    width: 16,
    height: 16,
    borderRadius: 5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  legendText: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: Paprika.textMuted,
  },
  pantryLink: {
    fontFamily: 'Inter-Medium',
    fontSize: 11,
    color: Paprika.textMuted,
    textDecorationLine: 'underline',
  },
});

export default RecipeCoverageBanner;
